<script setup>
import { useForm, usePage, router } from "@inertiajs/vue3";
import { ref, computed, watch } from "vue";
import Swal from "sweetalert2";

import EditIcons from "@/Components/Icons/EditIcons.vue";
import DeleteIcons from "@/Components/Icons/DeleteIcons.vue";
import Pagination from "@/Components/Pagination.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import ModalJenisDokumen from "@/Components/ModalJenisDokumen.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Header from "@/Components/Header.vue";
import Container from "@/Layouts/Container.vue";

const isModalOpen = ref(false);
const selectedJenis = ref(null);

const props = defineProps({
  jenisdoks: {
    type: Object,
    required: true,
  },
});

// Computed property for row number calculation with fallbacks
const getRowNumber = computed(() => {
  return (index) => {
    const currentPage = props.jenisdoks?.current_page || 1;
    const perPage = props.jenisdoks?.per_page || 10;
    return (currentPage - 1) * perPage + index + 1;
  };
});

let search = ref(usePage().props.search || ""),
  pageNumber = ref(1);

let studentUrl = computed(() => {
  let url = new URL(route("jenisdokumen.index"));
  url.searchParams.set("page", pageNumber.value);
  if (search.value) {
    url.searchParams.set("search", search.value);
  }
  return url;
});

const updatedPageNumber = (link) => {
  if (link && link.url) {
    try {
      const urlParams = new URLSearchParams(link.url.split("?")[1] || "");
      const page = urlParams.get("page");
      if (page) {
        pageNumber.value = parseInt(page);
      }
    } catch (error) {
      console.error("Error parsing pagination URL:", error);
    }
  }
};

watch(
  () => studentUrl.value,
  (updatedStudentUrl) => {
    router.visit(updatedStudentUrl.toString(), {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  }
);

watch(
  () => search.value,
  (value) => {
    if (value) {
      pageNumber.value = 1; // Reset to page 1 when search is updated
    }
  }
);

const form = useForm({
  id: null,
});

const deleteForm = useForm({});

/**
 * Edit jenis dokumen function
 * @param {object} jenis
 */
const editJenisDokumen = (jenis) => {
  form.reset();
  form.id = jenis.id;
  selectedJenis.value = jenis;
  isModalOpen.value = true;
};

/**
 * Delete jenis dokumen function
 * @param {number} jenisId
 */
const deleteJenisDokumen = (jenisId) => {
  Swal.fire({
    title: "Apakah Anda yakin?",
    text: "Jenis dokumen ini akan dihapus secara permanen!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  }).then((result) => {
    if (result.isConfirmed) {
      deleteForm.delete(route("jenisdokumen.destroy", jenisId), {
        onSuccess: () => {
          Swal.fire({
            title: "Berhasil!",
            text: "Jenis dokumen berhasil dihapus.",
            icon: "success",
            confirmButtonText: "OK",
          });
        },
        onError: () => {
          Swal.fire({
            title: "Gagal!",
            text: "Terjadi kesalahan saat menghapus jenis dokumen.",
            icon: "error",
            confirmButtonText: "OK",
          });
        },
      });
    }
  });
};
</script>

<template>
  <Head title="Jenis Dokumen" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Jenis Dokumen', href: route('jenisdokumen.index') },
          ]"
        />

        <div class="grid grid-cols-1 gap-6">
          <!-- Header Section -->
          <div class="mb-4 sm:mb-6">
            <div
              class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <h3 class="text-lg font-semibold text-gray-900">
                  Jenis Dokumen
                </h3>
                <p class="mt-1 text-sm text-gray-600">
                  Kelola jenis dokumen sistem
                </p>
              </div>
              <div class="mt-2 sm:mt-0">
                <button
                  v-if="$page.props.can.master_access"
                  class="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                  @click="isModalOpen = true"
                >
                  <i class="mr-2 fa-solid fa-plus"></i>
                  Tambah Jenis Dokumen
                </button>
              </div>
            </div>
          </div>

          <!-- Search Input -->
          <div class="relative mb-4">
            <div
              class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
            >
              <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
            </div>
            <input
              v-model="search"
              type="text"
              autocomplete="off"
              placeholder="Search data..."
              id="search"
              class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
            />
          </div>

          <!-- Table -->
          <div
            class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
          >
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      #
                    </th>
                    <th
                      scope="col"
                      class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[150px]"
                    >
                      Nama
                    </th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell min-w-[200px]"
                    >
                      Deskripsi
                    </th>
                    <th
                      scope="col"
                      class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[100px]"
                    >
                      <span class="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <!-- Table Rows -->
                  <tr
                    v-for="(jenisdok, index) in jenisdoks.data"
                    :key="jenisdok.id"
                    class="transition-colors duration-150 hover:bg-gray-50"
                  >
                    <td
                      class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                    >
                      {{ getRowNumber(index) }}
                    </td>
                    <td class="px-3 py-4 text-sm">
                      <div class="flex items-center">
                        <span class="mr-2 text-indigo-600">📄</span>
                        <span class="font-medium text-gray-900">
                          {{ jenisdok.nama }}
                        </span>
                      </div>
                      <!-- Mobile view: Show description under name on small screens -->
                      <div class="mt-1 text-xs text-gray-500 md:hidden">
                        {{ jenisdok.deskripsi }}
                      </div>
                    </td>
                    <td
                      class="hidden px-3 py-4 text-sm text-gray-500 whitespace-nowrap md:table-cell"
                    >
                      {{ jenisdok.deskripsi }}
                    </td>
                    <td
                      class="relative py-4 pl-3 pr-4 text-sm font-medium text-right sm:pr-6"
                    >
                      <div
                        class="flex items-center justify-end space-x-1 sm:space-x-2"
                      >
                        <!-- Action Buttons (Edit, Delete) -->
                        <button
                          v-if="$page.props.can.master_access"
                          @click="editJenisDokumen(jenisdok)"
                          class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 hover:text-indigo-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                          title="Edit"
                        >
                          <EditIcons class="w-3 h-3" />
                          <span class="hidden ml-1 sm:inline">Edit</span>
                        </button>
                        <button
                          v-if="$page.props.can.master_access"
                          @click="deleteJenisDokumen(jenisdok.id)"
                          class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 hover:text-red-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                          title="Delete"
                        >
                          <DeleteIcons class="w-3 h-3" />
                          <span class="hidden ml-1 sm:inline">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Pagination -->
          <Pagination
            :data="jenisdoks"
            :updatedPageNumber="updatedPageNumber"
          />
        </div>
      </div>
    </template>
  </Container>

  <ModalJenisDokumen
    v-model:isOpen="isModalOpen"
    :title="form.id ? 'Edit Jenis Dokumen' : 'Tambah Jenis Dokumen'"
    :jenis="form.id ? selectedJenis : null"
  />
</template>
